import {
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Technique,
  techniquesApi,
} from '@texas/api/endpoints/metadata/techniquesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import { TechniqueModal } from './TechniqueModal';

export function Techniques() {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Technique | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch, loading, error } = useApiResource(
    techniquesApi.getAllForOrganization,
  );

  useEffect(() => {
    refetch(currentUser.organizationId);
  }, [currentUser.organizationId, refetch]);

  const techniques = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    return (data ?? [])
      .filter((item) => !item.archived)
      .filter((item) =>
        [item.code, item.value, item.description].some((value) =>
          value?.toLowerCase().includes(normalized),
        ),
      )
      .sort((left, right) => left.code.localeCompare(right.code));
  }, [data, filter]);

  return (
    <ConfigurationPage
      title={t('configuration.techniques')}
      toolbar={
        <SearchFilterInput
          value={filter}
          style="entityDirectory"
          onChange={setFilter}
        />
      }
      action={
        <Button
          variant="entityDirectoryPrimary"
          onClick={() => {
            setSelected(null);
            onOpen();
          }}
        >
          {t('general.new')} {t('configuration.technique').toLowerCase()}
        </Button>
      }
    >
      <TechniqueModal
        isOpen={isOpen}
        onClose={onClose}
        onSaved={() => refetch(currentUser.organizationId)}
        technique={selected}
      />
      <Flex direction="column" gap="4">
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.code')}</Th>
              <Th>{t('general.value')}</Th>
              <Th>{t('configuration.vdcSupport')}</Th>
              <Th>{t('general.description')}</Th>
              <Th>{t('branch.displayOrder')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && techniques.length === 0 && (
              <Tr>
                <Td colSpan={5}>{t('general.noData')}</Td>
              </Tr>
            )}
            {techniques.map((technique) => (
              <Tr
                key={technique.id}
                cursor="pointer"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelected(technique);
                  onOpen();
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelected(technique);
                  onOpen();
                }}
              >
                <Td>{technique.code}</Td>
                <Td>{technique.value}</Td>
                <Td>{technique.vdcSupport ? t('general.enabled') : ''}</Td>
                <Td>{technique.description}</Td>
                <Td>{technique.displayOrder}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
